import { Sparkles, Droplets, ShieldCheck, Layers, Beaker, SprayCan, Wrench, Check, Leaf, Lightbulb } from 'lucide-react';

const AROMA_TAG = {
  agrumi: 'energia mediterranea',
  lavanda: 'freschezza rilassante',
  bergamotto: 'eleganza luminosa',
  'fiori-bianchi': 'delicatezza floreale',
  vaniglia: 'calore avvolgente',
  gelsomino: 'eleganza floreale',
};
const AROMA_ADJ = {
  agrumi: 'agrumata',
  lavanda: 'di lavanda',
  bergamotto: 'al bergamotto',
  'fiori-bianchi': 'ai fiori bianchi',
  vaniglia: 'alla vaniglia',
  gelsomino: 'al gelsomino',
};

export default function LavapavimentiDescription({ product }) {
  const aroma = product.fragranceLabel;
  const tag = AROMA_TAG[product.fragrance] || 'profumo persistente';
  const adj = AROMA_ADJ[product.fragrance] || `alla ${aroma.toLowerCase()}`;

  const vantaggi = [
    { I: Sparkles, t: 'Esperienza Olfattiva', d: `La fragranza ${adj} dona una scia vibrante e persistente, rendendo ogni stanza (domestica o professionale) piacevole e accogliente.` },
    { I: Droplets, t: 'Formula Universale', d: 'Progettato per essere efficace su tutti i tipi di pavimento. La sua azione quotidiana rimuove lo sporco con facilità, senza lasciare aloni.' },
    { I: ShieldCheck, t: 'Standard Elevati (HACCP)', d: 'Nonostante la piacevole profumazione, il prodotto è pienamente compatibile con i protocolli HACCP. Ideale per attività commerciali, ristoranti e uffici che devono garantire standard igienici rigorosi.' },
    { I: Layers, t: 'Versatilità 2-in-1', d: "Unisce le performance di un detergente industriale alla praticità e al piacere di un prodotto per la casa." },
  ];

  const uso = [
    { I: Beaker, t: 'Lavaggio Ordinario', d: "Diluire circa 30-50 ml di prodotto (circa un tappo dosatore) in un secchio da 5 litri d'acqua." },
    { I: SprayCan, t: 'Applicazione', d: 'Passare sulla superficie con mop, straccio o spugna. Grazie alla formula avanzata non è necessario risciacquare, garantendo un\'asciugatura rapida e senza aloni.' },
    { I: Droplets, t: 'Sporco Ostinato', d: 'In caso di macchie difficili, versare una piccola quantità di prodotto puro direttamente sulla zona interessata, strofinare e procedere al lavaggio abituale.' },
    { I: Wrench, t: 'Uso Meccanico', d: 'Grazie alla formula a bassa schiuma, può essere utilizzato con macchine lavasciuga professionali per grandi superfici.' },
  ];

  const tecnici = [
    ['Formato', 'Flacone da 1 kg.'],
    ['Fragranza', `${aroma} (persistenza garantita da estratti vegetali).`],
    ["Destinazione d'uso", 'Pavimenti domestici, uffici, ristorazione e strutture sanitarie.'],
    ['Certificazione', 'Prodotto idoneo per piani di autocontrollo HACCP.'],
    ['Sostenibilità', "Formula biodegradabile a base vegetale, sicura per l'uomo e rispettosa delle superfici trattate."],
  ];

  return (
    <section data-testid="lavapavimenti-description" className="mt-20">
      <div className="bg-essenya-ink text-white rounded-[2rem] p-8 md:p-14 relative overflow-hidden">
        <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-essenya-champagne/10 blur-3xl" />
        <div className="relative max-w-3xl">
          <div className="text-eyebrow text-white/60 mb-4">Descrizione</div>
          <h2 className="font-display text-3xl md:text-5xl leading-[1.08] font-medium mb-4">
            Lavapavimenti <span className="text-essenya-champagne">{aroma}</span>: igiene profonda, {tag} e sicurezza HACCP
          </h2>
          <p className="text-white/80 leading-relaxed">
            Trasforma la routine di pulizia in un'esperienza sensoriale rinvigorente senza rinunciare alla massima efficacia
            professionale. Questo detergente è stato studiato per chi cerca un pavimento impeccabile e un ambiente che profuma
            di fresco e di pulito.
          </p>
        </div>
      </div>

      <div className="mt-10">
        <h3 className="font-display text-2xl md:text-3xl mb-6">I vantaggi principali</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          {vantaggi.map(({ I, t, d }, i) => (
            <div key={i} data-testid={`lav-vantaggio-${i}`} className="bg-white rounded-2xl border border-essenya-border p-6 hover:shadow-lg transition-shadow duration-300">
              <div className="w-11 h-11 rounded-xl bg-essenya-surface2 flex items-center justify-center mb-4"><I size={20} strokeWidth={1.5} /></div>
              <p className="font-medium mb-1.5">{t}</p>
              <p className="text-essenya-text text-sm leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-10">
        <h3 className="font-display text-2xl md:text-3xl mb-2">Modalità d'uso</h3>
        <p className="text-essenya-text mb-6">Per ottenere il massimo della brillantezza e della persistenza del profumo, segui queste indicazioni.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          {uso.map(({ I, t, d }, i) => (
            <div key={i} data-testid={`lav-uso-${i}`} className="bg-essenya-surface2 rounded-2xl p-6">
              <I size={22} strokeWidth={1.5} className="mb-3 text-essenya-ink" />
              <p className="font-medium mb-1">{t}</p>
              <p className="text-essenya-text text-sm leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-10 grid lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-[2rem] border border-essenya-border p-8 md:p-10">
          <div className="flex items-center gap-3 mb-6"><ShieldCheck size={24} strokeWidth={1.5} /><h3 className="font-display text-2xl md:text-3xl">Dettagli tecnici e sicurezza</h3></div>
          <ul className="space-y-4">
            {tecnici.map(([k, v], i) => (
              <li key={i} data-testid={`lav-tech-${i}`} className="flex items-start gap-3">
                <span className="mt-0.5 w-6 h-6 rounded-full bg-essenya-ink text-white flex items-center justify-center flex-shrink-0"><Check size={14} strokeWidth={2.5} /></span>
                <span className="text-essenya-text leading-relaxed"><span className="text-essenya-ink font-medium">{k}:</span> {v}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="bg-essenya-champagne/30 rounded-[2rem] p-8 md:p-10 flex flex-col justify-center">
          <div className="flex items-center gap-3 mb-3"><Lightbulb size={22} strokeWidth={1.5} /><p className="font-display text-xl">Il consiglio in più</p></div>
          <p className="text-essenya-text leading-relaxed">
            Per una freschezza ancora più intensa, utilizza acqua tiepida: il calore aiuterà a sprigionare meglio le note
            aromatiche {adj} durante il lavaggio.
          </p>
          <div className="mt-6 inline-flex items-center gap-2 text-sm text-essenya-ink"><Leaf size={16} strokeWidth={1.5} /> Formula biodegradabile a base vegetale</div>
        </div>
      </div>
    </section>
  );
}
